/* eslint-disable arrow-body-style */
import { type ComponentPropsWithoutRef, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from './ErrorMessage';
import { textareaMaxLength } from '../../constants';
import {
  containsReservedCharacters,
  getCharacterCount,
  getInputRules,
  getWordCount,
  stripReservedCharacters,
  validateField,
} from '../../utils/form-utils';
import type { ErrorMessages, Rules } from '../../config/types';
import { parseEmailList } from '../../utils/email-list-utils';
import Details from './Details';

type Props = {
  name: string;
  numeric?: boolean;
  title: string;
  rules?: Rules;
  errorMessages?: ErrorMessages;
  detailsHeading?: string;
  detailsContent?: string;
  wordCount?: number;
  characterCount?: number;
  textRows?: number;
  emailList?: boolean;
  emailedAddresses?: string[];
} & ComponentPropsWithoutRef<'textarea'>;

const Textarea = (props: Props) => {
  const {
    name,
    title,
    rules,
    disabled,
    defaultValue,
    numeric,
    errorMessages,
    detailsHeading,
    detailsContent,
    wordCount,
    characterCount,
    textRows,
    emailList,
    emailedAddresses,
    ...textareaProps
  } = props;

  const { register, getFieldState, getValues, setValue, watch, formState } = useFormContext();

  const inputRules = useMemo(
    () => ({ ...getInputRules(rules ?? {}, title, textareaMaxLength) }),
    [rules, title],
  );

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue ?? '');
    }
  }, []);

  const hooksFormRegister = register(name, {
    validate: (value: string) => {
      const baseError = validateField(title, inputRules, value, errorMessages);
      if (baseError) return baseError;

      if (emailList && parseEmailList(String(value ?? '')).hasInvalidEntries) {
        return 'Check the email addresses highlighted in red';
      }

      const stringValue = typeof value === 'string' ? value : String(value ?? '');
      if (typeof wordCount === 'number') {
        const wordsUsed = getWordCount(stringValue);
        if (wordsUsed > wordCount) {
          const over = wordsUsed - wordCount;
          return `You have ${over} word${over === 1 ? '' : 's'} too many`;
        }
      }

      if (typeof characterCount === 'number') {
        const charsUsed = getCharacterCount(stringValue);
        if (charsUsed > characterCount) {
          const over = charsUsed - characterCount;
          return `You have ${over} character${over === 1 ? '' : 's'} too many`;
        }
      }

      return undefined;
    },
  });
  const { error } = getFieldState(name, formState);

  const rawValue = watch(name);
  const stringValue = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
  const wordsUsed = getWordCount(stringValue);
  const charsUsed = getCharacterCount(stringValue);
  const hasWordCount = typeof wordCount === 'number';
  const hasCharacterCount = typeof characterCount === 'number';
  const isWordOver = hasWordCount && wordsUsed > wordCount;
  const isCharacterOver = hasCharacterCount && charsUsed > characterCount;
  const parsedEmailList = useMemo(
    () => (emailList ? parseEmailList(stringValue) : null),
    [emailList, stringValue],
  );
  const emailValidationId = `${name}-email-list-validation`;
  const emailedAddressSet = useMemo(
    () => new Set((emailedAddresses || []).map((email) => email.toLocaleLowerCase('en-GB'))),
    [emailedAddresses],
  );

  const normalizeEmailList = () => {
    if (!parsedEmailList || parsedEmailList.normalizedValue === stringValue) return;
    setValue(name, parsedEmailList.normalizedValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <textarea
        {...textareaProps}
        {...hooksFormRegister}
        disabled={disabled ?? false}
        onBlur={(event) => {
          void hooksFormRegister.onBlur(event);
          normalizeEmailList();
        }}
        onKeyDown={(e) => {
          // The e.key values are strings for control characters such as 'Backspace'
          // All non control characters will have a single character length
          if (
            e.key.length === 1 &&
            containsReservedCharacters(e.key, inputRules.reservedCharacters)
          )
            e.preventDefault();
        }}
        onPaste={(e) => {
          const clipboardData = e.clipboardData.getData('text');
          if (containsReservedCharacters(clipboardData, inputRules.reservedCharacters)) {
            e.preventDefault();
            const sanitized = stripReservedCharacters(
              clipboardData,
              inputRules.reservedCharacters,
            );
            const target = e.target as HTMLTextAreaElement;
            const start = target.selectionStart ?? target.value.length;
            const end = target.selectionEnd ?? target.value.length;
            const nextValue = `${target.value.slice(0, start)}${sanitized}${target.value.slice(
              end,
            )}`;
            setValue(name, nextValue, { shouldDirty: true, shouldValidate: true });
            requestAnimationFrame(() => {
              const cursor = start + sanitized.length;
              target.selectionStart = cursor;
              target.selectionEnd = cursor;
            });
          }
        }}
        className="govuk-textarea"
        rows={textRows ?? 5}
        inputMode={numeric ? 'numeric' : 'text'}
        spellCheck={emailList ? false : textareaProps.spellCheck}
        autoCapitalize={emailList ? 'none' : textareaProps.autoCapitalize}
        autoCorrect={emailList ? 'off' : textareaProps.autoCorrect}
        aria-describedby={
          [
            textareaProps['aria-describedby'],
            parsedEmailList?.entries.length ? emailValidationId : '',
            hasCharacterCount ? `${name}-character-count` : '',
          ].filter(Boolean).join(' ') || undefined
        }
        data-testid={name}
      />
      {parsedEmailList && parsedEmailList.entries.length > 0 && (
        <div
          id={emailValidationId}
          className="email-list-validation"
          aria-live="polite"
          aria-label="Email address validation"
        >
          <p className="govuk-body-s govuk-!-font-weight-bold govuk-!-margin-bottom-2">
            Email address check
          </p>
          <ul className="email-list-validation__list">
            {parsedEmailList.entries.map((entry) => {
              const wasEmailed = emailedAddressSet.has(entry.value.toLocaleLowerCase('en-GB'));
              return (
                <li
                  className={`email-list-validation__entry email-list-validation__entry--${
                    entry.isValid ? 'valid' : 'invalid'
                  }`}
                  key={entry.value.toLocaleLowerCase('en-GB')}
                >
                  <span className="govuk-visually-hidden">
                    {entry.isValid ? 'Valid' : 'Invalid'} email address
                    {wasEmailed ? ', emailed' : ''}:{' '}
                  </span>
                  {entry.value}
                  {wasEmailed && (
                    <span className="email-list-validation__sent" aria-hidden="true">
                      {' '}✓
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {hasWordCount && (
        <p className={isWordOver ? 'govuk-error-message' : 'govuk-hint'} style={{ marginTop: 0 }}>
          {isWordOver
            ? `You have ${wordsUsed - wordCount} word${
                wordsUsed - wordCount === 1 ? '' : 's'
              } too many`
            : `You have ${wordCount - wordsUsed} word${
                wordCount - wordsUsed === 1 ? '' : 's'
              } remaining`}
        </p>
      )}
      {hasCharacterCount && (
        <p
          id={`${name}-character-count`}
          aria-live="polite"
          aria-atomic="true"
          className={isCharacterOver ? 'govuk-error-message' : 'govuk-hint'}
          style={{ marginTop: 0 }}
        >
          {isCharacterOver
            ? `You have ${charsUsed - characterCount} character${
                charsUsed - characterCount === 1 ? '' : 's'
              } too many`
            : `You have ${characterCount - charsUsed} character${
                characterCount - charsUsed === 1 ? '' : 's'
              } remaining`}
        </p>
      )}
    </>
  );
};
Textarea.displayName = 'Textarea';

export default Textarea;
